import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import {
	IGallery,
	INVITATION_OCCASION,
	INVITATION_TYPES,
	Invitations,
	LangType
} from "../../../../store/services/invitations/dto/invitations.dto";
import { Box, Divider, Typography } from "@mui/material";
import { TemplateWrapper } from "./styles";
import { curtains, flower01Url, flower02Url, waive01 } from "./constants";
import { LANG_AR, LANG_EN, RTL } from "../constants";
import MapComponent from "../Components/MapComponent/MapComponent";
import { getFieldMap } from "../../../../utils/inputs";
import AddIcon from "@mui/icons-material/Add";
import langEN from "../lang.EN.json";
import langAR from "../lang.AR.json";
import CountDown from "../CountDown";
import SaveTheDateBtn from "../Components/SaveTheDateBtn";
import Comments from "../Components/Comments";
import { getWeddingDate } from "../../../../utils/formatDate";
import MapButton from "../Components/MapButton";
import { stem } from "../Template_01/constants";
import InvitationGallery from "../../../InvitationGallery";
import { TemplateColorObject } from "../../../../InvitationColorMatrix";
import PartnersSocialList from "../Components/PartnersSocialList";
import Footer from "../Components/Footer";

const Template02 = forwardRef(
	(
		{
			data,
			gallery,
			colorMatrix,
			canEdit = false
		}: {
			data: Invitations;
			gallery: IGallery[];
			colorMatrix: TemplateColorObject;
			canEdit?: boolean;
		},
		ref
	) => {
		const canAnimate = !canEdit;
		const isTemplate = data?.type === INVITATION_TYPES.TEMPLATE;
		const lang = data?.language === LANG_EN ? langEN : langAR;
		const invitationId = data?._id || "";
		const fieldMap = getFieldMap(invitationId);

		const commentsSectionRef = useRef<HTMLElement>(null);
		const locationSectionRef = useRef<HTMLElement>(null);

		useImperativeHandle(ref, () => ({
			scrollToComments: () => {
				commentsSectionRef.current && commentsSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
			},
			scrollToLocation: () => {
				locationSectionRef.current && locationSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
			}
		}));

		const invitation_location_header = useMemo(() => {
			switch (data?.occasion) {
				case INVITATION_OCCASION.ENGAGEMENT: {
					return lang.engagement_location;
				}
				case INVITATION_OCCASION.KATB_KTAB: {
					return lang.katb_ktab_location;
				}
				default: {
					return lang.wedding_location;
				}
			}
		}, [data]);

		return (
			<>
				<TemplateWrapper id={"template_wrapper"} colormatrix={colorMatrix}>
					<Box className={"section_01"}>
						<Box className={"section_content"}>
							<Box className={"main_frame_wrapper"}>
								<img
									src={flower01Url}
									className={"flower_01 pulse_animation"}
									id={data?.fieldVisibility?.main_image?.hidden ? "main_image" : ""}
									alt="Flower"
								/>
								{data?.has_images && (canEdit || !data?.fieldVisibility?.main_image?.hidden) && (
									<>
										<Box
											className={`main_white_frame ${data?.fieldVisibility?.main_image?.hidden ? "shaded" : ""}`}
											data-dispatch-id={fieldMap.MAIN_IMAGE}
										>
											<img src={data?.main_image} className={"main_frame"} alt="partner" id={"main_image"} />
										</Box>
										<img src={flower02Url} className={"flower_02 breeze_animation"} alt="Flower" />
									</>
								)}
							</Box>
							<img src={curtains} className={"curtains"} alt="Curtains" />
							<Box className={`save_date_wrapper ${data?.language === LANG_AR && RTL}`}>
								<Box display="flex" gap={1} alignItems={"center"}>
									<Typography data-dispatch-id={fieldMap.GROOM_NAME} className={"partners_text guide_edit_field"}>
										{data?.groom_name}
									</Typography>

									<Typography className={"partners_text"}> {lang.and} </Typography>

									<Typography data-dispatch-id={fieldMap.BRIDE_NAME} className={"partners_text"}>
										{data?.bride_name}
									</Typography>
								</Box>
								{(canEdit || !data?.fieldVisibility?.save_the_date_text?.hidden) && (
									<Typography
										data-dispatch-id={fieldMap.SAVE_THE_DATE}
										className={`${data?.fieldVisibility?.save_the_date_text?.hidden ? "shaded" : ""}`}
									>
										{data?.save_the_date_text}
									</Typography>
								)}

								<br />

								<span className={"date"} data-dispatch-id={fieldMap.WEDDING_DATE}>
									{getWeddingDate(data?.wedding_date, data?.language, data?.timezone, isTemplate)}
								</span>

								<CountDown
									data={data || ({} as Invitations)}
									daysString={lang.days}
									hoursString={lang.hours}
									minutesString={lang.mins}
									secondsString={lang.sec}
								/>
								<SaveTheDateBtn canEdit={canEdit} data={data} btnText={lang.save_the_date_button_text} />
							</Box>
						</Box>
					</Box>
					<Box className={"section_02"}>
						{/*<img src={waive01} className={"waive"} alt="Waive" />*/}
						<Box className={"section_content"}>
							<Box className={"fader"}></Box>
							{data?.is_islamic && (
								<Typography className={"quran"}>
									بَارَكَ اللهُ لَكُما، وَبَارَكَ عَلَيْكُما، وَجَمَعَ بَيْنَكُما فِي خَيْرٍ
								</Typography>
							)}
							{(canEdit || !data?.fieldVisibility?.intro_text?.hidden) && (
								<Typography
									data-dispatch-id={fieldMap.INTRO_TEXT}
									className={`main_intro ${data?.fieldVisibility?.intro_text?.hidden ? "shaded" : ""} animate-on-scroll delay-1`}
								>
									{data?.intro_text}
								</Typography>
							)}
							{(canEdit || !data?.fieldVisibility?.sub_intro_text?.hidden) && (
								<Typography
									data-dispatch-id={fieldMap.SUB_INTRO}
									className={`main_sub_intro ${data?.fieldVisibility?.sub_intro_text?.hidden ? "shaded" : ""} animate-on-scroll delay-2`}
								>
									{data?.sub_intro_text}
								</Typography>
							)}

							<Box height={20} />
							{data?.has_images && (canEdit || !data?.fieldVisibility?.groom_image?.hidden) && (
								<Box
									data-dispatch-id={fieldMap.GROOM_IMAGE}
									className={`partner_frame_wrapper ${data?.fieldVisibility?.groom_image?.hidden ? "shaded" : ""}`}
								>
									<img src={data?.groom_image} className={"groom"} alt="groom" />
								</Box>
							)}

							<Typography data-dispatch-id={fieldMap.GROOM_NAME} className={"partner_name"}>
								{data?.groom_name}
							</Typography>

							<PartnersSocialList
								socials={data?.partner_socials_1 || []}
								canEdit={canEdit}
								dispatchId={fieldMap.SOCIAL_MEDIA_1}
							/>
							<Typography className={"and"}>&</Typography>
							{data?.has_images && (canEdit || !data?.fieldVisibility?.bride_image?.hidden) && (
								<Box
									className={`partner_frame_wrapper ${data?.fieldVisibility?.bride_image?.hidden ? "shaded" : ""}`}
									data-dispatch-id={fieldMap.BRIDE_IMAGE}
								>
									<img src={data?.bride_image} className={"bride"} alt="bride" />
								</Box>
							)}

							<Typography className={"partner_name"} data-dispatch-id={fieldMap.BRIDE_NAME}>
								{data?.bride_name}
							</Typography>

							<PartnersSocialList
								socials={data?.partner_socials_2 || []}
								canEdit={canEdit}
								dispatchId={fieldMap.SOCIAL_MEDIA_2}
							/>
						</Box>
					</Box>
					{data?.is_islamic && (
						<Box className={"section_03"}>
							{/*<img src={waive02} className={"waive"} alt="Waive" />*/}
							<Typography className={"quran_start"}>بِسْمِ اللهِ الرَّحْمَنِ الرَّحِيمِِِ</Typography>
							<Typography className={"quran"}>
								﴿ وَمِنْ آيَاتِهِ أَنْ خَلَقَ لَكُم مِّنْ أَنفُسِكُمْ أَزْوَاجًا لِّتَسْكُنُوا إِلَيْهَا وَجَعَلَ
								بَيْنَكُم مَّوَدَّةً وَرَحْمَةً ۚ إِنَّ فِي ذَٰلِكَ لَآيَاتٍ لِّقَوْمٍ يَتَفَكَّرُون َ﴾
							</Typography>
							<Typography className={"quran_end"}>صَدَقَ اللهُ العَظِيمُ</Typography>
						</Box>
					)}
					{!data?.fieldVisibility?.loveStory?.hidden && (
						<Box className={"section_04"}>
							<Box className={"section_content"}>
								<img src={flower02Url} className={"flower_03 pulse_animation"} alt="Flower" />
								<Typography className={"section_title"}>{lang.our_love_story}</Typography>
								{data?.has_images && (canEdit || !data?.fieldVisibility?.story_image?.hidden) && (
									<Box className={`love_story_wrapper ${data?.fieldVisibility?.story_image?.hidden ? "shaded" : ""}`}>
										<img
											src={data?.story_image}
											data-dispatch-id={fieldMap.STORY_IMAGE}
											className="story_image"
											alt="Love Story"
										/>
									</Box>
								)}

								{data?.story.length > 0 && (
									<Box className={"stories"} data-dispatch-id={fieldMap.STORY}>
										{data?.story.map((story, index) => (
											<Box key={story._id} className={`story animate-on-scroll delay-${index + 1}`}>
												<Typography>{story.title}</Typography>
												<Divider />
												<Typography>{story.description}</Typography>
											</Box>
										))}
									</Box>
								)}
								{canEdit && (data?.story || []).length === 0 && (
									<Box my={3} data-dispatch-id={fieldMap.STORY}>
										<Box className={"add_story"}>
											<AddIcon />
										</Box>
									</Box>
								)}
							</Box>
						</Box>
					)}

					{!data?.fieldVisibility?.gallery?.hidden && data?.has_images && (
						<Box className={"section_05"}>
							<Typography className={"section_title"}>{lang.our_gallery}</Typography>
							<img src={stem} className={"stem"} alt="flower stem" />
							<Box height={30}></Box>
							<Box data-dispatch-id={fieldMap.GALLERY}>
								<InvitationGallery gallery={gallery || []} canEdit={canEdit} />
							</Box>
						</Box>
					)}

					{!data?.fieldVisibility?.comments?.hidden && (
						<Box className={"section_06"} ref={commentsSectionRef}>
							<Box className={"fader"}></Box>
							<Typography className={"section_title"} textAlign={"center"}>
								{lang.comments}
							</Typography>
							<Box height={10} />
							{(canEdit || !data?.fieldVisibility?.comments_text?.hidden) && (
								<>
									<Typography
										data-dispatch-id={fieldMap.COMMENTS_TEXT}
										className={`comment_text ${data?.fieldVisibility?.comments_text?.hidden ? "shaded" : ""} animate-on-scroll delay-1`}
									>
										{data?.comments_text}
									</Typography>

									<Box height={20} />
								</>
							)}

							{(canEdit || !data?.fieldVisibility?.comments_sub_text?.hidden) && (
								<>
									<Typography
										data-dispatch-id={fieldMap.COMMENTS_SUB_TEXT}
										className={`comment_sub_text ${data?.fieldVisibility?.comments_sub_text?.hidden ? "shaded" : ""} animate-on-scroll delay-2`}
									>
										{data?.comments_sub_text}
									</Typography>
									<Box height={20} />
								</>
							)}

							<Comments canEdit={canEdit} language={data?.language as LangType} colormatrix={colorMatrix} />
						</Box>
					)}
					<Box className={"section_07"} ref={locationSectionRef}>
						<Box className={"section_content"}>
							{!data?.fieldVisibility?.location?.hidden && (
								<>
									<Typography className={"section_title"}>{invitation_location_header}</Typography>
									<Box className={"location_wrapper"}>
										<Box height={20} />
										{(canEdit || !data?.fieldVisibility?.wedding_location_text?.hidden) && (
											<Typography
												data-dispatch-id={fieldMap.LOCATION_TEXT}
												className={`location_text ${data?.fieldVisibility?.wedding_location_text?.hidden ? "shaded" : ""}`}
											>
												{data?.wedding_location_text}
											</Typography>
										)}
										<Box height={20} />
										<Box data-dispatch-id={fieldMap.MAP}>
											<MapButton canEdit={canEdit} text={lang.location} />
										</Box>
									</Box>
									<Box data-dispatch-id={fieldMap.MAP}>
										<MapComponent canEdit={canEdit} />
									</Box>
								</>
							)}
							<Box height={20} />
							{data?.has_images && (canEdit || !data?.fieldVisibility?.main_image?.hidden) && (
								<Box
									data-dispatch-id={fieldMap.MAIN_IMAGE}
									className={`partner_04_wrapper ${data?.fieldVisibility?.main_image?.hidden ? "shaded" : ""}`}
								>
									<img src={data?.main_image} alt="couple" className={"partner_04"} />
								</Box>
							)}

							<Box
								my={3}
								display="flex"
								gap={1}
								alignItems={"center"}
								className={`${data?.language === LANG_AR && RTL} animate-on-scroll delay-1`}
							>
								<Typography data-dispatch-id={fieldMap.GROOM_NAME} className={"partners_text"}>
									{data?.groom_name}
								</Typography>

								<Typography className={"partners_text"}> {lang.and} </Typography>

								<Typography data-dispatch-id={fieldMap.BRIDE_NAME} className={"partners_text"}>
									{data?.bride_name}
								</Typography>
							</Box>

							{(canEdit || !data?.fieldVisibility?.conclusion_text?.hidden) && (
								<Typography
									data-dispatch-id={fieldMap.CONCLUSION_TEXT}
									className={`conclusion_text ${data?.fieldVisibility?.conclusion_text?.hidden ? "shaded" : ""} animate-on-scroll delay-2`}
								>
									{data?.conclusion_text}
								</Typography>
							)}
						</Box>
						<img src={flower02Url} className={"flower_04 breeze_animation"} alt="Flower" />
						<img src={flower02Url} className={"flower_05 breeze_animation"} alt="Flower" />
					</Box>
					<Footer colormatrix={colorMatrix} />
				</TemplateWrapper>
			</>
		);
	}
);

export default Template02;
